import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowLeft, CheckCircle2, X } from 'lucide-react';
import { products } from '../data/mock';
import ErrorModal from '../components/ErrorModal';
import SEO, { breadcrumbSchema } from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';

/* ── Find a model across all pre-assembled series ── */
const findModel = (modelName) => {
  const preAssembled = products.find((p) => p.id === 'pre-assembled');
  if (!preAssembled) return null;
  for (const series of preAssembled.models) {
    const item = series.items.find((i) => i.name === modelName);
    if (item) return { ...item, series: series.series };
  }
  return null;
};

/* ── Image viewer: technical diagram ── */
const ImageViewer = ({ model }) => {
  return (
    <div className="sticky top-20">
      {/* Technical diagram */}
      <div className="relative bg-white rounded-2xl overflow-hidden aspect-[4/3] border border-gray-100">
        <img
          src={model.diagram}
          alt={`${model.name} — Technical Diagram`}
          className="w-full h-full object-contain p-4"
        />
      </div>
    </div>
  );
};

/* ── Main Order page ── */
const Order = () => {
  const [searchParams] = useSearchParams();
  const { tx, t, language } = useLanguage();
  const { formatPrice, currency } = useCurrency();
  const fr = language === 'fr';
  const modelName = searchParams.get('model');
  const model = modelName ? findModel(decodeURIComponent(modelName)) : null;

  const [selectedOptions, setSelectedOptions] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [errors, setErrors] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [invalidFields, setInvalidFields] = useState([]);

  const options = []; // User will add options later

  const basePrice = model?.price || 0;
  const optionsTotal = selectedOptions.reduce((sum, o) => sum + o.price, 0);
  const total = basePrice + optionsTotal;

  const toggleOption = (opt) => {
    setSelectedOptions((prev) =>
      prev.find((o) => o.id === opt.id)
        ? prev.filter((o) => o.id !== opt.id)
        : [...prev, opt]
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    
    // Clear error when field becomes valid
    if (name === 'name' && value.trim()) {
      setInvalidFields(prev => prev.filter(f => f !== 'name'));
    } else if (name === 'email' && value.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setInvalidFields(prev => prev.filter(f => f !== 'email'));
    } else if (name === 'phone' && value.trim()) {
      setInvalidFields(prev => prev.filter(f => f !== 'phone'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    const validationErrors = [];
    const invalid = [];
    
    if (!formData.name.trim()) {
      validationErrors.push(t('validation.nameRequired'));
      invalid.push('name');
    }
    if (!formData.email.trim()) {
      validationErrors.push(t('validation.emailRequired'));
      invalid.push('email');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      validationErrors.push(t('validation.emailInvalid'));
      invalid.push('email');
    }
    if (!formData.phone.trim()) {
      validationErrors.push(t('validation.phoneRequired'));
      invalid.push('phone');
    }
    if (!agreed) {
      validationErrors.push(t('validation.agreeTerms'));
    }
    
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      setInvalidFields(invalid);
      setShowErrorModal(true);
      return;
    }
    
    setSubmitting(true);
    try {
      // Step 1: Save to backend FIRST to get order ID
      let orderId = null;
      try {
        const dbRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'order',
            customer_name: formData.name,
            customer_email: formData.email,
            customer_phone: formData.phone,
            customer_company: formData.company,
            product: model?.name || '',
            series: model?.series || '',
            message: formData.message,
            source_page: '/order',
            details: {
              basePrice: formatPrice(basePrice),
              selectedOptions: selectedOptions.map((o) => o.name).join(', ') || 'None',
              vehicle: [formData.vehicleYear, formData.vehicleMake, formData.vehicleModel].filter(Boolean).join(' ') || 'Not specified',
              bedType: formData.bedType || 'Not specified',
              cabType: formData.cabType || 'Not specified',
              totalPrice: formatPrice(total),
            },
          }),
        });
        const dbData = await dbRes.json();
        orderId = dbData.id;
      } catch (dbErr) {
        console.warn('Backend save failed:', dbErr);
      }

      // Step 2: Send to Web3Forms with admin dashboard link
      const adminLink = orderId
        ? `${window.location.origin}/portal/admin?tab=orders&order=${orderId}`
        : '';
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: '4deb5ebc-3d5b-446d-9890-9acae8b33da4',
          subject: `Order Inquiry - ${model?.name}`,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          message: formData.message,
          model: model?.name,
          series: model?.series,
          basePrice: formatPrice(basePrice),
          selectedOptions: selectedOptions.map((o) => o.name).join(', ') || 'None',
          vehicle: [formData.vehicleYear, formData.vehicleMake, formData.vehicleModel].filter(Boolean).join(' ') || 'Not specified',
          bedType: formData.bedType || 'Not specified',
          cabType: formData.cabType || 'Not specified',
          deposit: formatPrice(1500),
          totalPrice: formatPrice(total),
          'View in Admin Dashboard': adminLink || 'Link unavailable',
        }),
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
        setSelectedOptions([]);
        setInvalidFields([]);
      } else {
        // Parse API errors
        const apiErrors = [];
        const apiInvalid = [];
        
        if (data.message) {
          if (data.message.includes('email')) {
            apiErrors.push(t('validation.emailInvalidOrder'));
            apiInvalid.push('email');
          } else if (data.message.includes('phone')) {
            apiErrors.push(t('validation.phoneInvalidOrder'));
            apiInvalid.push('phone');
          } else {
            apiErrors.push(`${fr ? 'Échec de la soumission' : 'Order submission failed'}: ${data.message}`);
          }
        } else {
          apiErrors.push(t('validation.orderSubmitFailed'));
        }
        
        setErrors(apiErrors);
        setInvalidFields(apiInvalid);
        setShowErrorModal(true);
      }
    } catch (err) {
      console.error(err);
      setErrors([t('validation.networkError')]);
      setShowErrorModal(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (!model) {
    return (
      <div className="min-h-screen pt-36 flex flex-col items-center justify-center">
        <p className="text-gray-500 mb-4">{fr ? 'Modèle introuvable.' : 'Model not found.'}</p>
        <Link to="/products" className="text-sm font-semibold" style={{ color: '#E8520A' }}>
          {fr ? '← Retour aux produits' : '← Back to Products'}
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 bg-white">
      <SEO
        title="Order Your Camper"
        description="Configure and order your Total Composites expedition truck camper. Choose your model, options, and customizations. Get a personalized quote for your build."
        path="/order"
        keywords="order truck camper, buy expedition camper, Total Composites order form, Lynx Wolf Wolverine camper order, custom truck camper Canada"
        jsonLd={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Products', path: '/products' },
          { name: 'Order', path: '/order' }
        ])}
      />
      {/* Top bar */}
      <div className="border-b border-gray-100 bg-white sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3">
          <Link
            to="/products"
            className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft size={14} /> {fr ? 'Retour aux produits' : 'Back to Products'}
          </Link>
          <span className="text-gray-300">|</span>
          <span className="text-xs font-bold uppercase tracking-widest text-gray-400">{tx(model.series)}</span>
          <span className="text-gray-300">/</span>
          <span className="text-xs font-bold text-gray-800">{model.name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid lg:grid-cols-2 gap-12 items-start">

          {/* LEFT — Single camper image */}
          <div className="sticky top-20 self-start">
            <div className="bg-gray-50 rounded-2xl overflow-hidden aspect-square w-full max-w-md mx-auto">
              <img
                src={model.image}
                alt={model.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* RIGHT — Configurator */}
          <div>
            {/* Model header */}
            <div className="mb-6">
              <span
                className="inline-block text-xs font-bold uppercase tracking-widest px-2.5 py-1 rounded-full mb-3"
                style={{ backgroundColor: '#FFF0E8', color: '#E8520A' }}
              >
                {tx(model.series)}
              </span>
              <h1 className="text-3xl lg:text-4xl font-black text-gray-900 mb-1 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
                {model.name}
              </h1>
              <p className="text-gray-500 text-sm">{tx(model.fit)}</p>
            </div>

            {/* Base price */}
            <div className="flex items-end justify-between border border-gray-100 rounded-xl p-5 mb-6 bg-gray-50">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">{t('orderExtras.basePrice')}</p>
                <p className="text-2xl font-black text-gray-900">
                  {formatPrice(basePrice)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">{t('orderExtras.emptyShell')}</p>
                <p className="text-xs text-gray-500">{t('orderExtras.emptyShellDesc')}</p>
              </div>
            </div>

            {/* Options placeholder */}
            {options.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-4">{fr ? 'Ajouter des options' : 'Add Options'}</h3>
                <div className="space-y-3">
                  {options.map((opt) => {
                    const isSelected = selectedOptions.find((o) => o.id === opt.id);
                    return (
                      <div
                        key={opt.id}
                        className={`border rounded-xl p-4 transition-all duration-200 ${
                          isSelected ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex gap-3 items-start">
                            {opt.image && (
                              <img src={opt.image} alt={opt.name} className="w-14 h-14 object-contain rounded-lg bg-gray-100 p-1 flex-shrink-0" />
                            )}
                            <div>
                              <p className="text-sm font-bold text-gray-900">{opt.name}</p>
                              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{opt.description}</p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2 flex-shrink-0">
                            <p className="text-sm font-black text-gray-900">+{formatPrice(opt.price, { showCurrency: false })}</p>
                            <button
                              onClick={() => toggleOption(opt)}
                              className={`px-3 py-1.5 text-xs font-bold rounded-full border transition-all duration-150 whitespace-nowrap ${
                                isSelected
                                  ? 'text-orange-700 border-orange-300 bg-orange-100 hover:bg-orange-200'
                                  : 'text-white border-transparent hover:opacity-90'
                              }`}
                              style={!isSelected ? { backgroundColor: '#E8520A' } : {}}
                            >
                              {isSelected ? 'Remove' : 'Add Option'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Price summary */}
            {selectedOptions.length > 0 && (
              <div className="border border-gray-200 rounded-xl p-5 mb-6 bg-gray-50 space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{fr ? 'Prix de base' : 'Base price'}</span>
                  <span>{formatPrice(basePrice, { showCurrency: false })}</span>
                </div>
                {selectedOptions.map((o) => (
                  <div key={o.id} className="flex justify-between text-sm text-gray-600">
                    <span>{o.name}</span>
                    <span>+{formatPrice(o.price, { showCurrency: false })}</span>
                  </div>
                ))}
                <div className="border-t border-gray-200 pt-2 flex justify-between font-black text-gray-900">
                  <span>Total</span>
                  <span>{formatPrice(total)}</span>
                </div>
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-gray-100 my-6" />

            {/* Order form */}
            <h3 className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-5">{t('orderExtras.yourInfo')}</h3>

            {/* Honeypot for spam protection - hidden from users */}
            <input type="checkbox" name="botcheck" style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />

            {submitted ? (
                <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl px-5 py-5">
                <CheckCircle2 size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-green-800 font-bold text-sm mb-1">{t('orderExtras.preOrderSubmitted')}</p>
                  <p className="text-green-700 text-xs leading-relaxed">
                    {fr
                      ? <>Merci. Un représentant vous contactera sous peu pour examiner et confirmer les détails de votre commande <strong>{model.name}</strong> et organiser le paiement du dépôt de {formatPrice(1500)}.</>
                      : <>Thank you. A representative will contact you shortly to review and confirm your <strong>{model.name}</strong> order details and arrange your {formatPrice(1500)} deposit payment.</>
                    }
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">
                      {fr ? 'Nom' : 'Name'} <span style={{ color: '#E8520A' }}>*</span>
                    </label>
                    <input
                      type="text" name="name" value={formData.name} onChange={handleChange} required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none transition-all"
                      onFocus={(e) => (e.target.style.borderColor = '#E8520A')}
                      onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                      placeholder={fr ? 'Nom complet' : 'Full name'}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">
                      {fr ? 'Courriel' : 'Email'} <span style={{ color: '#E8520A' }}>*</span>
                    </label>
                    <input
                      type="email" name="email" value={formData.email} onChange={handleChange} required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none transition-all"
                      onFocus={(e) => (e.target.style.borderColor = '#E8520A')}
                      onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">{fr ? 'Téléphone' : 'Phone'} <span style={{ color: '#E8520A' }}>*</span></label>
                    <input
                      type="tel" name="phone" value={formData.phone} onChange={handleChange} required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none transition-all"
                      onFocus={(e) => (e.target.style.borderColor = '#E8520A')}
                      onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">{fr ? 'Entreprise' : 'Company'}</label>
                    <input
                      type="text" name="company" value={formData.company} onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none transition-all"
                      onFocus={(e) => (e.target.style.borderColor = '#E8520A')}
                      onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                      placeholder="Company name"
                    />
                  </div>
                </div>
                {/* Vehicle Information */}
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-600">{t('orderExtras.vehicleInfo')}</p>
                  </div>
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">{t('orderExtras.year')} <span style={{ color: '#E8520A' }}>*</span></label>
                        <input
                          type="text" name="vehicleYear" value={formData.vehicleYear || ''} onChange={handleChange} required
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none transition-all"
                          onFocus={(e) => (e.target.style.borderColor = '#E8520A')}
                          onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                          placeholder="2024"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">{t('orderExtras.make')} <span style={{ color: '#E8520A' }}>*</span></label>
                        <input
                          type="text" name="vehicleMake" value={formData.vehicleMake || ''} onChange={handleChange} required
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none transition-all"
                          onFocus={(e) => (e.target.style.borderColor = '#E8520A')}
                          onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                          placeholder="Ford"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">{t('orderExtras.modelLabel')} <span style={{ color: '#E8520A' }}>*</span></label>
                        <input
                          type="text" name="vehicleModel" value={formData.vehicleModel || ''} onChange={handleChange} required
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none transition-all"
                          onFocus={(e) => (e.target.style.borderColor = '#E8520A')}
                          onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                          placeholder="F-150"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">{t('orderExtras.bedType')}</label>
                        <select
                          name="bedType" value={formData.bedType || ''} onChange={handleChange}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none transition-all bg-white text-gray-700"
                          onFocus={(e) => (e.target.style.borderColor = '#E8520A')}
                          onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                        >
                          <option value="">{t('orderExtras.selectBedType')}</option>
                          <option>Standard</option>
                          <option>Long</option>
                          <option>Flatbed</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">{t('orderExtras.cabType')}</label>
                        <select
                          name="cabType" value={formData.cabType || ''} onChange={handleChange}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none transition-all bg-white text-gray-700"
                          onFocus={(e) => (e.target.style.borderColor = '#E8520A')}
                          onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                        >
                          <option value="">{t('orderExtras.selectCabType')}</option>
                          <option>Single Cab</option>
                          <option>Extended Cab</option>
                          <option>Crew Cab</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">{fr ? 'Message' : 'Message'}</label>
                  <textarea
                    name="message" value={formData.message} onChange={handleChange} rows={3}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none transition-all resize-none"
                    onFocus={(e) => (e.target.style.borderColor = '#E8520A')}
                    onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                    placeholder={fr ? 'Questions ou exigences spéciales...' : 'Any questions or special requirements...'}
                  />
                </div>

                {/* Add-ons */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
                    {t('orderExtras.addOnsTitle')}
                  </label>
                  <p className="text-xs text-gray-400 mb-3 leading-relaxed">
                    {t('orderExtras.addOnsDesc')}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'Windows', label: t('orderExtras.windows') },
                      { key: 'Roof Hatch', label: t('orderExtras.roofHatch') },
                      { key: 'Water Boiler', label: t('orderExtras.waterBoiler') },
                      { key: 'Water Filtration System', label: t('orderExtras.waterFiltration') },
                      { key: 'Diesel Heater', label: t('orderExtras.dieselHeater') },
                      { key: 'Bed Slats', label: t('orderExtras.bedSlats') },
                      { key: 'Recovery Board', label: t('orderExtras.recoveryBoard') },
                    ].map(({ key: addon, label: addonLabel }) => {
                      const isOn = formData.addons?.includes(addon);
                      return (
                        <label
                          key={addon}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all duration-150 ${
                            isOn ? 'border-orange-400 bg-orange-50' : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <div
                            className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center transition-all duration-150 ${
                              isOn ? 'border-transparent' : 'border-gray-300'
                            }`}
                            style={isOn ? { backgroundColor: '#E8520A', borderColor: '#E8520A' } : {}}
                          >
                            {isOn && (
                              <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                                <path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={!!isOn}
                            onChange={(e) => {
                              const current = formData.addons || [];
                              setFormData((p) => ({
                                ...p,
                                addons: e.target.checked
                                  ? [...current, addon]
                                  : current.filter((a) => a !== addon),
                              }));
                            }}
                          />
                          <span className={`text-sm font-medium ${isOn ? 'text-orange-700' : 'text-gray-700'}`}>
                            {addonLabel}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Deposit box */}
                <div className="rounded-xl border-2 p-5 flex items-center justify-between" style={{ borderColor: '#E8520A', backgroundColor: '#FFF8F5' }}>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#E8520A' }}>{t('orderExtras.deposit')}</p>
                    <p className="text-2xl font-black text-gray-900">
                      {formatPrice(1500)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{t('orderExtras.balanceDue')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400 mb-1">{t('orderExtras.orderTotal')}</p>
                    <p className="text-sm font-bold text-gray-700">
                      {formatPrice(total)}
                    </p>
                    {selectedOptions.length > 0 && (
                      <p className="text-xs text-gray-400">+ {selectedOptions.length} option{selectedOptions.length > 1 ? 's' : ''}</p>
                    )}
                  </div>
                </div>

                {/* Disclaimer */}
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-xs text-gray-500 leading-relaxed space-y-2">
                  <p className="font-bold text-gray-600 uppercase tracking-widest text-xs mb-2">{t('orderExtras.disclaimer')}</p>
                  <p>{t('orderExtras.disclaimerText1')}</p>
                  <p>{t('orderExtras.disclaimerText2')}</p>
                  <p>{t('orderExtras.disclaimerText3')}</p>
                  <p>{t('orderExtras.disclaimerText4')}</p>
                  <p>{t('orderExtras.disclaimerText5')}</p>
                </div>

                {/* Agreement checkbox */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex-shrink-0 mt-0.5">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="sr-only"
                      required
                    />
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-150 ${
                        agreed ? 'border-transparent' : 'border-gray-300 bg-white group-hover:border-gray-400'
                      }`}
                      style={agreed ? { backgroundColor: '#E8520A', borderColor: '#E8520A' } : {}}
                    >
                      {agreed && (
                        <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                          <path d="M1 4L4 7.5L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-gray-600 leading-relaxed">
                    {t('orderExtras.agreeTerms')}
                    <span style={{ color: '#E8520A' }}> *</span>
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={submitting || !agreed}
                  className="w-full py-3.5 font-bold text-white rounded-full text-sm transition-all duration-200 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#E8520A' }}
                >
                  {submitting ? (fr ? 'Envoi en cours…' : 'Submitting…') : (fr ? 'Soumettre' : 'Submit')}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
      
      {/* Error Modal */}
      <ErrorModal 
        show={showErrorModal} 
        onClose={() => setShowErrorModal(false)} 
        errors={errors} 
      />
    </div>
  );
};

export default Order;
